import * as Logger from "js-logger";
import * as PIXI from 'pixi.js';
import { AssetConfigSchema } from "slotworks/config/asset-config-schema";
import AnimatedSprite from "slotworks/graphics/animation/animated-sprite";
import AnimationManager from "slotworks/graphics/animation/animation-manager";
import CanvasManager from "slotworks/graphics/canvas/canvas-manager";
import Layers from "slotworks/graphics/layers/layers";
import DeviceInfo from "slotworks/utils/device-info";
import { FatalErrorCommand } from "slotworks/commands/error/fatal-error-command";
import { Sprite } from "pixi.js";
import gameLoop from "slotworks/core/game-loop";
import settingsModel from "slotworks/model/settings/settings-model";

declare const __DEBUG__: boolean;

class GraphicsManager {

    private textures: Map<string, PIXI.Texture> = new Map<string, PIXI.Texture>();

    private config: AssetConfigSchema;

    private prefix: string = "portrait-";

    /**
     * Call this after preloading.
     *
     * Creates a pixi renderer (WebGL with canvas fallback), an HTML canvas element and a pixi stage container
     * Uses graphics config to intialize factory methods
     * Starts render loop (requestAnimationFrame)
     *
     * @param config {GraphicsConfigSchema}
     */
    public init(config: AssetConfigSchema) {
        const layoutConfig = config.layouts[DeviceInfo.layoutId].graphics;

        Layers.init(layoutConfig);

        const animationConfig = config.layouts.all.animations;

/////////////////////////
        if (__DEBUG__) {
            this.listAllFonts(config);
        }
//////////////////

        AnimationManager.init(animationConfig);
        this.config = config;

        // todo make it on layout change
    //    CanvasManager.onOrientationChange.add(this.refresh, this);
        DeviceInfo.onLayoutChange.add(this.refresh, this);
    }

    public refresh() {
        const layoutConfig = this.config.layouts[DeviceInfo.layoutId].graphics;
        Layers.refresh(layoutConfig);
    }

    /**
     * Registers a texture with the graphics manager
     *
     * @param id {string}
     * @param texture {PIXI.Texture}
     */
    public addTexture(id: string, texture: PIXI.Texture) {        
        const prepare = CanvasManager.renderer.plugins.prepare as any; // PIXI.prepare.WebGLPrepare;
        prepare.upload(texture.baseTexture);

        this.textures.set(id, texture);
    }

    /**
     * Returns a reference to a texture previously registered
     * Don't use this method for sprite creation, instead use createSprite factory method
     *
     * @param id {string}
     * @returns {PIXI.Texture}
     */
    public getTexture(id: string): PIXI.Texture {
        if (!this.textures.has(id)) {    
            this.onMissingAsset("GraphicsManager: Texture `" + id + "` does not exist in texture cache");
        }
        return this.textures.get(id);
    }

    /**
     * Registers a texture with the graphics manager
     *
     * @param id {string}
     * @param texture {PIXI.Texture}
     */
    public destroyTexture(id: string) {
        const texture = this.getTexture(id);
        texture.destroy(true);

        this.textures.delete(id);
    }

    /**
     * Factory method to instantiate a new Sprite from a given ID
     * Note the textures it uses are from the pixi cache, but the sprite object itself is a new object in memory
     *
     * @param id {string}
     * @returns {PIXI.Sprite}
     */
    public createSprite(id: string): PIXI.Sprite;

    /**
     * Factory method to instantiate a new Sprite from a list of IDs, will keep trying until it finds one which exists, or return null
     * Note the textures it uses are from the pixi cache, but the sprite object itself is a new object in memory
     *
     * @param ids {string}
     * @returns {PIXI.Sprite}
     */
    public createSprite(ids: string[]): PIXI.Sprite;

    public createSprite(id: string | string[]): PIXI.Sprite {
        let ids;
        if (typeof id === "string") {
            ids = [id];
        } else {
            ids = id;
        }

        let sprite = null;

        for (const spriteId of ids) {

            if (!this.textures.has(spriteId)) {
                if (typeof id === "string") {
                    this.onMissingAsset("GraphicsManager: Invalid texture id: " + id);
                }
            } else {
                sprite = new PIXI.Sprite(this.textures.get(spriteId));
            }
        }

        if(!sprite) {
            this.onMissingAsset("GraphicsManager: Invalid texture ids: " + id);
        }

        return sprite;
    }

    /**
     * Creates an animation by name
     * @param id {string}
     */
    public createAnimation(id: string): AnimatedSprite;

    /**
     * Goes through the list and returns the first valid animation
     * @param ids {string[]}
     */
    public createAnimation(ids: string[]): AnimatedSprite;

    /**
     * Factory method to instantiate a new AnimatedSprite.
     * Note the textures it uses are from the pixi cache, but the sprite object itself is a new object in memory
     *
     * @param id {string}
     * @returns {AnimatedSprite}
     */
    public createAnimation(ids: string | string[]): AnimatedSprite {
        if (typeof ids === "string") {
            return AnimationManager.generateAnimation(ids);
        } else {
            return AnimationManager.generateAnimationWithFallbacks(ids);
        }
    }

    /**
     * Purely used for testing purposes
     * @param font
     * @param size
     * @param background {number} Optionally adds a full screen background color
     */
    public testBitmapFont(font: string, size: number, testString: string = "£123,456,789.00", background?: number) {

        if (background !== null) {
            const bg = new PIXI.Graphics();
            bg.beginFill(background);
            bg.drawRect(0, 0, CanvasManager.stageWidth, CanvasManager.stageHeight);
            bg.endFill();
            CanvasManager.stage.addChild(bg);
        }

        const text = new PIXI.BitmapText(testString,
            {
                fontSize: size,
                fontName: font
            });

        // CanvasManager.stage.addChild(text);

        const bounds = text.getBounds();
        text.x = (CanvasManager.stageWidth - bounds.width) * 0.5;
        text.y = (CanvasManager.stageHeight - bounds.height) * 0.5;
    }

    /**
     * Check if texture is available
     * 
     * @param id {string}
     */
    public hasTexture(id: string): boolean {
        return this.textures.has(id);
    }    

    private listAllFonts(config: AssetConfigSchema) {
        const fontDictionary = new Map<string, boolean>();

        for (const layoutName in config.layouts) {
            if (config.layouts.hasOwnProperty(layoutName)) {
                const layout = config.layouts[layoutName];

                if (layout.graphics) {
                    for (const graphicName in layout.graphics) {
                        if (layout.graphics.hasOwnProperty(graphicName)) {
                            const graphic = layout.graphics[graphicName];
                            if (graphic.text) {
                                for (const text of graphic.text) {
                                    fontDictionary.set(text.fontFamily + "-" + text.fontVariant, true);
                                }
                            }
                        }
                    }
                }
            }
        }

        Logger.info("Used fonts:");
        fontDictionary.forEach((value, index) => Logger.info(index));
    }

    private onMissingAsset(message: string) {        
        FatalErrorCommand("err.ref");
        gameLoop.setPaused(true);
        throw new GraphicsManagerError(message);        
    }
}

export default new GraphicsManager();

/**
 * Blocks the game when error occured
 */
export class GraphicsManagerError extends Error {
    
    public graphicError: boolean = true;

    constructor(message: string) {
        super(message);
    }
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./~/slotworks/graphics/graphics-manager.ts